/*eslint-disable*/
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

// components
import {
  Grid,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material'
import TextInputField from '../textInputField'
import SelectField from '../selectField'
import GroupsField from '../groupsField'
import ButtonWithLoader from '../buttonWithLoader'
import PreferredLanguageField from '../preferredLanguageField'
import SearchIcon from '@mui/icons-material/Search'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
// constants
import { LOCALES } from '../../constants/general'
import { getDomainNames } from '../../helpers/general'

// helpers
import {
  validateUserChange,
  hasJnjGroupAccess,
} from './helpers/userForm.validators'
import { useIsMobileView } from '../../utils/isMobileView'
import { isSuperAdmin } from '../../helpers/user'

//restricted tenant
import { isRestrictedTenant } from '../../helpers/users'

//css styles
import styles from './UserForm.module.scss'
import { noop } from '../../utils/general'
import { fetchCountries } from '../../services/data'
import InputLabel from '../inputLabel'

const cancelStyle = {
  backgroundColor: 'rgba(98, 81, 55, 0.1)',
  '&:hover': {
    backgroundColor: 'rgba(102,90,71, 0.20)',
  },
  '&:disabled': {
    backgroundColor: 'rgba(138, 115, 92, 0.05)',
  },
  '&:focus': {
    backgroundColor: 'rgba(112, 99, 79, 0.1)',
  },
}

const submitStyle = {
  backgroundColor: '#eb1700',
  '&:hover': {
    backgroundColor: '#CC1400',
  },
  '&:disabled': {
    backgroundColor: '#665A47',
  },
  '&:focus': {
    backgroundColor: '#CC1400',
  },
}

function UserForm(props) {
  const { t } = useTranslation()
  const isMobileView = useIsMobileView()
  const jnjGroupAccess = hasJnjGroupAccess()
  const superAdmin = isSuperAdmin()
  const [countriesList, setCountriesList] = useState([])
  const [localeList, setLocaleList] = useState([])
  const NONE_OPTION = t('None')
  const [localeDisplayValue, setLocaleDisplayValue] = useState(null)
  const [localeNoneSelected, setLocaleNoneSelected] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState('')
  const [countriesLoading, setCountriesLoading] = useState(false)
  const {
    user,
    setUser,
    errors,
    onSubmit,
    isSubmitting,
    setErrors,
    onCancel,
    formFieldsConfig,
    primaryButtonLabel,
    areFormValuesChanged,
    getSelectedGroupsCount,
    groupsModalInfo,
    emailRegex,
    isCreateUser,
    existingUser,
  } = props

  useEffect(() => {
    getCountriesList()
  }, [])

  useEffect(() => {
    if (!localeList || localeList.length === 0) {
      setLocaleDisplayValue(null)
      return
    }

    if (user.locale) {
      setLocaleNoneSelected(false)
      setLocaleDisplayValue(user.locale)
      return
    }

    setLocaleDisplayValue(localeNoneSelected ? NONE_OPTION : null)
  }, [localeList, user.locale, localeNoneSelected])

  useEffect(() => {
    if (user) {
      setSelectedLanguage(user.preferredLanguage)
    }
  }, [user])

  const getCountriesList = async () => {
    setCountriesLoading(true)
    try {
      const response = await fetchCountries()
      if (response && response.status === 200) {
        setCountriesList(response.data[0])
        setLocaleList(response.data[1])
        setCountriesLoading(false)
      }
    } catch (error) {
      setCountriesList([])
      setCountriesLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    const updatedUser = { ...user, [name]: value }
    const errorMsgs = validateUserChange(
      updatedUser,
      errors,
      name,
      emailRegex,
      jnjGroupAccess || superAdmin,
      existingUser,
    )
    setErrors(errorMsgs)
    setUser(updatedUser)
  }

  const handleSelectChange = (name) => (e) => {
    const { value } = e?.target || {}
    const updatedUser = { ...user, [name]: value }
    setUser(updatedUser)
  }

  const handleSelectedLanguage = (_event, value) => {
    const updatedUser = {
      ...user,
      preferredLanguage: value ? value : '',
    }

    setSelectedLanguage(value)
    setUser(updatedUser)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit()
  }

  const handleCancel = (e) => {
    e.preventDefault()
    onCancel()
  }

  return (
    <Grid className={isMobileView ? styles.containerMobile : styles.container}>
      <Grid className={styles.formContainer}>
        <TextInputField
          className={styles.userInput}
          label={t('first-name')}
          id={'firstName'}
          required={true}
          value={user.firstName}
          error={errors.firstName}
          onChange={handleChange}
        />
        <TextInputField
          label={t('last-name')}
          id={'lastName'}
          required={isRestrictedTenant()}
          value={user.lastName}
          error={errors.lastName}
          onChange={handleChange}
        />
        <TextInputField
          label={t('email')}
          id={'email'}
          required={true}
          value={user.email}
          error={errors.email}
          onChange={handleChange}
        />
        <TextInputField
          label={t('phone-number-create-user')}
          id={'phoneNumber'}
          required={false}
          value={user.phoneNumber}
          error={errors.phoneNumber}
          onChange={handleChange}
        />
        <div className={styles.autoCompleteInput}>
          <InputLabel
            label={t('country-optional-user')}
            className={styles.selectLabel}
          />
          <Autocomplete
            disablePortal
            id="countries"
            value={
              countriesList.length > 0
                ? countriesList.find((v) => v.code === user.country)
                : null
            }
            options={countriesList}
            //loading={countriesLoading}
            getOptionLabel={(option) => `${option.country} (${option.code})`}
            onChange={(event, value) => {
              const updatedUser = {
                ...user,
                country: value?.code ? value.code : '',
              }
              setUser(updatedUser)
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {countriesLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                placeholder={t('select-an-option-user')}
                error={errors.country}
                size="small"
              />
            )}
          />
        </div>
        <div className={styles.autoCompleteInput}>
          <InputLabel
            label={t('local-optional-user')}
            className={styles.selectLabel}
          />
          <Autocomplete
            disablePortal
            id="locales"
            value={localeDisplayValue}
            options={[NONE_OPTION, ...localeList]}
            //loading={countriesLoading}
            onChange={(event, value) => {
              if (value === NONE_OPTION) {
                setLocaleNoneSelected(true)
                setLocaleDisplayValue(NONE_OPTION)
                setUser({ ...user, locale: null })
                return
              }

              if (!value) {
                setLocaleNoneSelected(false)
                setLocaleDisplayValue(null)
                setUser({ ...user, locale: null })
                return
              }
              setLocaleNoneSelected(false)
              setLocaleDisplayValue(value)
              setUser({ ...user, locale: value })
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {countriesLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                placeholder={t('select-an-option-user')}
                error={errors.locale}
                size="small"
              />
            )}
          />
        </div>
        <div className={styles.autoCompleteInput}>
          <InputLabel
            label={t('preferred-language-create-user')}
            className={styles.selectLabel}
          />
          <PreferredLanguageField
            handleSelectedLanguage={handleSelectedLanguage}
            selectedLanguage={selectedLanguage}
            preferredLanguage={
              user.preferredLanguage !== undefined ? user.preferredLanguage : ''
            }
          />
        </div>
        <GroupsField
          user={user}
          setUser={setUser}
          getSelectedGroupsCount={getSelectedGroupsCount}
          groupsModalInfo={groupsModalInfo}
          isCreateUser={isCreateUser}
        />
        {formFieldsConfig?.showDomainName && (
          <SelectField
            // sx={{ marginTop: '15px' }}
            options={getDomainNames()}
            label={t('send-activation-email-through-create-user')}
            id={'domainName'}
            value={user.domainName}
            error={errors.domainName}
            onChange={handleSelectChange('domainName')}
          />
        )}
      </Grid>
      <Grid className={styles.buttonContainer}>
        <ButtonWithLoader
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={!areFormValuesChanged}
          label={primaryButtonLabel}
          className="primary-btn"
          sx={{
            ...submitStyle,
            marginTop: '20px',
            width: '120px',
            height: '40px',
          }}
        />
        <Button
          onClick={handleCancel}
          type="submit"
          className="secondary-btn"
          variant="contained"
          disabled={isSubmitting}
          disableElevation={true}
          sx={{
            ...cancelStyle,
            marginTop: '20px',
            marginLeft: '10px',
            width: '120px',
            height: '42px',
            color: '#000000',
          }}
        >
          {t('group-modal-cancel-cta')}
        </Button>
      </Grid>
    </Grid>
  )
}

UserForm.propTypes = {
  formFieldsConfig: PropTypes.object,
  primaryButtonLabel: PropTypes.string,
  areFormValuesChanged: PropTypes.bool,
  getSelectedGroupsCount: PropTypes.func,
  isCreateUser: PropTypes.bool.isRequired,
}

UserForm.defaultProps = {
  formFieldsConfig: {},
  primaryButtonLabel: i18next.t('create-view-user'),
  areFormValuesChanged: false,
  getSelectedGroupsCount: noop,
}

export default UserForm
